import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchCourseById, updateCourse } from "../../apis/CourseApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useTheme } from "../Theme/ThemeContext";
import { SkeletonFormPage } from "../Skeletons";
import SeoMeta from "../SeoMeta";

export default function EditCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isPremium: false,
    orderId: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Load course data
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    
    const loadCourse = async () => {
      try {
        const course = await fetchCourseById(courseId);
        if (isMounted) {
          setFormData({
            title: course.title || "",
            description: course.description || "",
            isPremium: course.isPremium || false,
            orderId: course.orderId || "",
          });
        }
      } catch (err) {
        console.error("Error loading course:", err);
        if (isMounted) {
          errorToast("Failed to load course");
          navigate("/courses");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadCourse();
    return () => { isMounted = false; };
  }, [courseId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Course title is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Course description is required";
    }
    if (!formData.orderId || formData.orderId <= 0) {
      newErrors.orderId = "Please enter a valid order number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const courseData = {
        title: formData.title,
        description: formData.description,
        isPremium: formData.isPremium,
        orderId: parseInt(formData.orderId),
        updatedBy: user?.userId,
        updatedAt: Date.now(),
      };

      await updateCourse(courseId, courseData, user?.token);
      successToast("Course updated successfully!");
      navigate(`/courses/${courseId}`);
    } catch (err) {
      console.error("Error updating course:", err);
      errorToast("Failed to update course. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <SkeletonFormPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoMeta
        title="Edit Course - Code Arena Admin"
        description="Edit course details"
        url={`https://www.code-arena.in/courses/edit/${courseId}`}
        image="/logo192.png"
      />

      <div className="ca-container max-w-3xl py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="flex items-center gap-2 font-medium mb-6 transition-colors"
          style={{ color: 'var(--color-primary)' }}
        >
          <FaArrowLeft /> Back to Course
        </button>

        {/* Form Card */}
        <div className="ca-card overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} px-4 sm:px-8 py-5 sm:py-6`}>
            <h1 className="text-3xl font-bold text-white">Edit Course</h1>
            <p className="text-blue-100 mt-2">Update course details</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
            {/* Course Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Course Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`ca-input py-3 ${
                  errors.title
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Course Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Course Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="5"
                className={`ca-input py-3 ${
                  errors.description
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.description && (
                <p className="text-red-600 text-sm mt-1">{errors.description}</p>
              )}
            </div>

            {/* Order Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Order Number *
              </label>
              <input
                type="number"
                name="orderId"
                value={formData.orderId}
                onChange={handleInputChange}
                min="1"
                className={`ca-input py-3 ${
                  errors.orderId
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.orderId && (
                <p className="text-red-600 text-sm mt-1">{errors.orderId}</p>
              )}
            </div>

            {/* Is Premium Toggle */}
            <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                name="isPremium"
                id="isPremium"
                checked={formData.isPremium}
                onChange={handleInputChange}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="isPremium" className="flex-1 cursor-pointer">
                <p className="font-semibold text-gray-900">Premium Course</p>
                <p className="text-sm text-gray-600">
                  Mark this course as premium if it requires payment
                </p>
              </label>
              {formData.isPremium && (
                <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  Premium
                </span>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/courses/${courseId}`)}
                className="ca-btn-secondary flex-1 px-6 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ca-btn-primary flex-1 px-6 py-3"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
